import React, { useState, useEffect } from 'react';
import { DatePicker, Button, Spin, message, Card, Space, Typography, Tag, Row, Col, Grid } from 'antd';
import { UserOutlined, PhoneOutlined, TeamOutlined, CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { dutyScheduleService, ViewDutySchedule, DutyPositionWithEmployees, EmployeeDetail } from '../services/dutyScheduleService';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ViewDutyScheduleTab: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
    const [scheduleData, setScheduleData] = useState<ViewDutySchedule | null>(null);
    const [loading, setLoading] = useState(false);
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const fetchSchedule = async (date: Dayjs) => {
        try {
            setLoading(true);
            const data = await dutyScheduleService.getViewDutySchedule(date);
            if (data?.isApproved) {
                setScheduleData(data);
            } else {
                setScheduleData(null);
            }
        } catch (error) {
            message.error('Không tải được lịch trực');
            setScheduleData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedDate) {
            fetchSchedule(selectedDate);
        }
    }, [selectedDate]);

    const handleDateChange = (date: Dayjs | null) => {
        setSelectedDate(date);
    };

    return (
        <div style={{ padding: isMobile ? 12 : 24 }}>
            <Card 
                style={{ marginBottom: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                        <DatePicker
                            style={{ width: '100%' }}
                            value={selectedDate}
                            onChange={handleDateChange}
                            format="DD/MM/YYYY"
                            placeholder="Chọn ngày"
                            suffixIcon={<CalendarOutlined style={{ color: '#1890ff' }} />}
                            inputReadOnly={isMobile}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Button 
                            type="primary" 
                            onClick={() => selectedDate && fetchSchedule(selectedDate)}
                            style={{ width: isMobile ? '100%' : 'auto' }}
                            icon={<CalendarOutlined />}
                        >
                            Tải lịch trực
                        </Button>
                    </Col>
                </Row>
            </Card>

            <Spin spinning={loading}>
                {scheduleData && (scheduleData.dutyPositions.length > 0 || scheduleData.isApproved) && (
                    <Card 
                        style={{ 
                            marginBottom: 24, 
                            borderRadius: 8, 
                            borderLeft: `4px solid ${scheduleData.isApproved ? '#52c41a' : '#faad14'}`
                        }}
                    >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                <Title level={4} style={{ margin: 0 }}>
                                    Lịch trực ngày {dayjs(scheduleData.date).format('dddd, DD/MM/YYYY')}
                                </Title>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {scheduleData.isApproved ? (
                                    <Tag 
                                        icon={<CheckCircleOutlined />} 
                                        color="success"
                                        style={{ marginRight: 8, padding: '4px 8px' }}
                                    >
                                        Đã duyệt
                                    </Tag>
                                ) : (
                                    <Tag 
                                        icon={<ClockCircleOutlined />} 
                                        color="warning"
                                        style={{ marginRight: 8, padding: '4px 8px' }}
                                    >
                                        Chưa duyệt
                                    </Tag>
                                )}
                                {scheduleData.isApproved && scheduleData.approverName && (
                                    <Text type="secondary">
                                        Bởi {scheduleData.approverName} lúc {dayjs(scheduleData.approvalDate).format('HH:mm DD/MM/YYYY')}
                                    </Text>
                                )}
                            </div>
                        </Space>
                    </Card>
                )}

                {(!scheduleData || scheduleData.dutyPositions.length === 0) && !loading && (
                    <Card style={{ textAlign: 'center', borderRadius: 8 }}>
                        <Text type="secondary">Không có lịch trực cho ngày đã chọn.</Text>
                    </Card>
                )}

                <Row gutter={[16, 16]}>
                    {scheduleData?.dutyPositions.map((position: DutyPositionWithEmployees) => (
                        <Col xs={24} sm={24} md={12} lg={8} xl={6} key={position.vitriTrucId}>
                            <Card
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                        <Text strong style={{ fontSize: '1.1rem' }}>
                                            {position.moTa || position.tenViTri}
                                        </Text>
                                    </div>
                                }
                                style={{ 
                                    height: '100%',
                                    borderRadius: 8,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}
                                bodyStyle={{ padding: '16px' }}
                                headStyle={{ borderBottom: '1px solid #f0f0f0' }}
                            >
                                {position.employees.length > 0 ? (
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        {position.employees.map((employee: EmployeeDetail) => (
                                            <Card 
                                                key={employee.maChamCong}
                                                style={{ 
                                                    backgroundColor: '#f9f9f9',
                                                    borderRadius: 6,
                                                    border: '1px solid #f0f0f0',
                                                    marginBottom: 8
                                                }}
                                                bodyStyle={{ padding: '12px' }}
                                            >
                                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                                        <Text strong style={{ color: '#1f1f1f' }}>{employee.tenNV}</Text>
                                                    </div>
                                                    {employee.tenKK && (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <TeamOutlined style={{ marginRight: 8, color: '#5a5a5a' }} />
                                                            <Text style={{ color: '#434343' }}>{employee.tenKK}</Text>
                                                        </div>
                                                    )}
                                                    {employee.dienThoai && (
                                                        <div style={{ marginTop: 8 }}>
                                                            <a href={`tel:${employee.dienThoai}`} style={{ textDecoration: 'none' }}>
                                                                <Tag 
                                                                    icon={<PhoneOutlined />} 
                                                                    color="blue" 
                                                                    style={{ 
                                                                        cursor: 'pointer', 
                                                                        padding: '6px 12px', 
                                                                        borderRadius: '20px', 
                                                                        fontSize: '14px',
                                                                        width: '100%',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        margin: 0,
                                                                        border: '1px solid #91d5ff',
                                                                        transition: 'all 0.3s'
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f0f5ff'}
                                                                >
                                                                    <Text strong style={{ color: '#1890ff', marginLeft: 4 }}>{employee.dienThoai}</Text>
                                                                </Tag>
                                                            </a>
                                                        </div>
                                                    )}
                                                    {employee.isKem && (
                                                        <Tag 
                                                            color="blue" 
                                                            style={{ 
                                                                marginTop: 4,
                                                                alignSelf: 'flex-start',
                                                                borderRadius: 4
                                                            }}
                                                        >
                                                            Trực kèm
                                                        </Tag>
                                                    )}
                                                </Space>
                                            </Card>
                                        ))}
                                    </Space>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                        <Text type="secondary">Chưa có nhân viên trực</Text>
                                    </div>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Spin>
        </div>
    );
};

export default ViewDutyScheduleTab;
